import { useCallback, useMemo } from 'react';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';
import { useDeviceSelector } from 'js/store/hooks';
import { prop, pipe, map } from 'ramda';
import { useBonuses } from 'js/components/main/BonusHistory/hooks/useBonuses';
import { useShowTermsPopup } from 'js/components/main/BonusHistory/hooks/useShowTermsPopup';
import { useCancelBonusPopup } from '../useCancelBonusPopup';

import style from '../../BonusHistory.scss';
import { getTableAccordionDataCreator } from '@theme/js/components/common/Table/accordionFactory/tableDataCreator';
import { getHeaderCellsCreator } from '../../cellMappers/getHeaderCellsCreator';
import { getRowCellsCreator } from '../../../../common/Table/accordionFactory/cellMappers/getRowCellsCreator';
import { getFormattedData } from '../../formatters/getFormattedData';
import { useFormatter } from '@core/js/hooks/useFormatter';
import { IMoneyInfo } from '@core/js/hooks/socket/types';
import { ICurrentBonusParsed, useBonusesFormatter } from '@theme/js/hooks/swiss/useBonusFormatter';

export const useTableData = () => {
    const rawBonuses = useBonuses();
    const bonuses = useBonusesFormatter(rawBonuses as unknown as ICurrentBonusParsed[], true);
    const translate = useComponentTranslations();
    const showTermsPopup = useShowTermsPopup();
    const showCancelBonusPopup = useCancelBonusPopup();
    const formatter = useFormatter();
    const isDesktop = useDeviceSelector(prop('isDesktop'));

    const formatCurrency = useCallback(
        ({ amount, currencyCode }: IMoneyInfo): string =>
            formatter.formatCurrency(Number(amount), { currencyCode }),
        [formatter]
    );

    const headerCellsCreator = useMemo(() => getHeaderCellsCreator({
        style,
        showTermsPopup,
        showCancelBonusPopup,
        isDesktop,
        translate
    }), [isDesktop, showCancelBonusPopup, showTermsPopup, translate]);

    const rowCellsCreator = useMemo(() => getRowCellsCreator({
        showCancelBonusPopup,
        showTermsPopup,
        style,
        translate
    }), [showCancelBonusPopup, showTermsPopup, translate]);

    const dataFormatter = useMemo(() => getFormattedData({
        translate, isDesktop, formatCurrency }), [formatCurrency, isDesktop, translate]);

    const tableAccordionDataCreator = useMemo(() => getTableAccordionDataCreator({
        pathToId: 'remoteBonusId',
        createHeaderCells: headerCellsCreator,
        createRowCells: pipe(dataFormatter, data => data.filter(pipe(prop('value'), Boolean)), map(rowCellsCreator))
    }), [dataFormatter, headerCellsCreator, rowCellsCreator]);

    return useMemo(() => tableAccordionDataCreator(bonuses), [bonuses, tableAccordionDataCreator]);
};
